/**
 * Swift type mapper - Functional implementation with dependency injection
 * Maps TypeScript types to Swift types with support for different contexts
 */

import type { InterfaceDefinition } from "../../parser";
import { isAnyObjectType } from "../../utils/type-helpers";

export interface SwiftTypeMappingOptions {
  /**
   * Module name for prefixing generated types
   */
  moduleName?: string;
  /**
   * Interface definitions for resolving custom types
   */
  interfaces?: InterfaceDefinition[];
  /**
   * Whether to use Objective-C compatible types
   */
  useObjCTypes?: boolean;
  /**
   * Whether to use Codable-compatible types for structs
   */
  useCodableTypes?: boolean;
}

export interface SwiftTypeMapper {
  /**
   * Maps TypeScript types to Swift types (for Codable structs)
   */
  mapTypeScriptToSwift(tsType: string): string;

  /**
   * Maps TypeScript types to Swift types for @objc compatibility
   */
  mapTypeScriptToSwiftForObjC(tsType: string): string;

  /**
   * Enhanced Swift type mapping that recognizes interface types
   */
  mapTypeScriptToSwiftWithInterfaces(type: string): string;

  /**
   * Maps TypeScript types to Objective-C types
   */
  mapTypeScriptToObjC(
    tsType: string,
    isReturnType?: boolean,
    isAsync?: boolean,
  ): string;

  /**
   * Maps TS types for callback argument positions to Swift types
   */
  mapTsToSwiftForCallbackArg(tsType: string): string;

  /**
   * Gets Swift default value for a type
   */
  getSwiftDefaultValue(swiftType: string): string;

  /**
   * Gets Objective-C default return value for a type
   */
  getDefaultReturnValue(tsType: string): string;

  /**
   * Capitalizes first character of a string
   */
  capitalizeFirst(str: string): string;
}

/**
 * Creates a Swift type mapper with the given options
 */
export function createSwiftTypeMapper(
  options: SwiftTypeMappingOptions = {},
): SwiftTypeMapper {
  const {
    moduleName,
    interfaces = [],
    useObjCTypes = false,
    useCodableTypes = false,
  } = options;

  /**
   * Capitalizes first character of a string
   */
  function capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Maps TypeScript types to Swift types (for Codable structs)
   */
  function mapTypeScriptToSwift(tsType: string): string {
    // Handle Promise types
    if (tsType.startsWith("Promise<") && tsType.endsWith(">")) {
      const innerType = tsType.slice(8, -1);
      return mapTypeScriptToSwift(innerType);
    }

    // Basic type mapping
    switch (tsType) {
      case "string":
        return "String";
      case "number":
        return "Double";
      case "boolean":
        return "Bool";
      case "void":
        return "Void";
      case "Any":
        return "Any";
      case "any":
        // Fallback for TypeScript's built-in 'any' - prefer using explicit 'Any' type
        return "Any";
      default:
        // Array types
        if (tsType.includes("[]")) {
          const elementType = tsType.replace("[]", "");
          return `[${mapTypeScriptToSwift(elementType)}]`;
        }

        // Object types - should be handled by proper struct generation
        if (tsType.startsWith("{") && tsType.endsWith("}")) {
          // Fallback to String dictionary to remain Codable in generated structs
          return "[String: String]";
        }

        // Check if it's a PascalCase interface type
        if (tsType.match(/^[A-Z][a-zA-Z0-9]*$/)) {
          // Add module prefix if provided
          if (moduleName) {
            const pascalModuleName = capitalizeFirst(moduleName);
            return `${pascalModuleName}${tsType}`;
          }
          return tsType;
        }

        return "[String: String]"; // Default Codable type
    }
  }

  /**
   * Maps TypeScript types to Swift types for @objc compatibility
   * Uses [String: Any] for complex types instead of Swift structs
   */
  function mapTypeScriptToSwiftForObjC(tsType: string): string {
    // Callback/function types are bridged as RCTResponseSenderBlock in @objc surface
    if (tsType === "callback") {
      return "RCTResponseSenderBlock";
    }

    // Handle Promise types
    if (tsType.startsWith("Promise<") && tsType.endsWith(">")) {
      const innerType = tsType.slice(8, -1);
      return mapTypeScriptToSwiftForObjC(innerType);
    }

    // Basic type mapping (same as regular mapping)
    switch (tsType) {
      case "string":
        return "String";
      case "number":
        return "Double";
      case "boolean":
        return "Bool";
      case "void":
        return "Void";
      case "Any":
        return "Any";
      case "any":
        // Fallback for TypeScript's built-in 'any' - prefer using explicit 'Any' type
        return "Any";
      case "AnyObject":
        return "[String: Any]";
      default:
        // Array types
        if (tsType.includes("[]")) {
          const elementType = tsType.replace("[]", "");
          // Check if element type is complex
          if (
            (elementType.startsWith("{") && elementType.endsWith("}")) ||
            elementType.match(/^[A-Z][a-zA-Z0-9]*$/)
          ) {
            return `[Any]`; // Array of complex objects
          } else {
            return `[${mapTypeScriptToSwiftForObjC(elementType)}]`;
          }
        }

        // Object types or interface types - use [String: Any] for @objc compatibility
        if (
          (tsType.startsWith("{") && tsType.endsWith("}")) ||
          (tsType.match(/^[A-Z][a-zA-Z0-9]*$/) && tsType !== "AnyObject")
        ) {
          return "[String: Any]";
        }

        return "[String: Any]"; // Default for unknown types
    }
  }

  /**
   * Enhanced Swift type mapping that recognizes interface types
   */
  function mapTypeScriptToSwiftWithInterfaces(type: string): string {
    // Handle Promise types - unwrap first
    if (type.startsWith("Promise<") && type.endsWith(">")) {
      const innerType = type.slice(8, -1);
      return mapTypeScriptToSwiftWithInterfaces(innerType);
    }

    // Check for explicit Any type first
    if (type === "Any" || type === "any") {
      return "Any";
    }

    // Check for special generic object types (AnyObject, {})
    if (isAnyObjectType(type)) {
      return "[String: Any]";
    }

    // Check if this type is defined as an interface
    const interfaceNames = new Set(interfaces.map((i) => i.name));

    // If this is an interface type, add module prefix
    if (interfaceNames.has(type)) {
      if (moduleName) {
        const pascalModuleName = capitalizeFirst(moduleName);
        return `${pascalModuleName}${type}`;
      }
      return type;
    }

    // Handle basic types
    const basicMapping = mapTypeScriptToSwift(type);
    if (basicMapping !== "[String: String]") {
      return basicMapping;
    }

    // If it's a PascalCase type (likely an interface), add module prefix
    if (type.match(/^[A-Z][a-zA-Z0-9]*$/)) {
      if (moduleName) {
        const pascalModuleName = capitalizeFirst(moduleName);
        return `${pascalModuleName}${type}`;
      }
      return type;
    }

    return "[String: String]";
  }

  /**
   * Maps TypeScript types to Objective-C types
   */
  function mapTypeScriptToObjC(
    tsType: string,
    isReturnType: boolean = false,
    isAsync: boolean = false,
  ): string {
    // Handle Promise types
    if (tsType.startsWith("Promise<") && tsType.endsWith(">")) {
      return "void"; // async functions return void
    }

    // Handle callback/function types
    if (tsType === "callback") {
      return "RCTResponseSenderBlock";
    }

    // Basic type mapping
    switch (tsType) {
      case "string":
        return "NSString *";
      case "number":
        if (isReturnType && !isAsync) {
          return "NSNumber *"; // sync function number returns are NSNumber *
        }
        return "double"; // parameters and async are double
      case "boolean":
        if (isReturnType && !isAsync) {
          return "NSNumber *"; // sync function boolean returns are NSNumber *
        }
        return "BOOL"; // parameters are BOOL
      case "void":
        return "void";
      case "any":
        return "id";
      default:
        // Array types
        if (tsType.includes("[]")) {
          return "NSArray *";
        }
        // Object types
        if (tsType.startsWith("{") && tsType.endsWith("}")) {
          return isReturnType ? "id" : "id"; // complex objects are id
        }
        return "id";
    }
  }

  /**
   * Maps TS types for callback argument positions to Swift types
   * Use [String: Any] for inline objects to avoid Codable constraints in closures
   */
  function mapTsToSwiftForCallbackArg(tsType: string): string {
    const t = tsType.trim();

    // Primitives
    if (t === "string") return "String";
    if (t === "number") return "Double";
    if (t === "boolean") return "Bool";
    if (t === "void") return "Void";

    // Arrays
    if (t.endsWith("[]")) {
      const elem = t.slice(0, -2);
      return `[${mapTsToSwiftForCallbackArg(elem)}]`;
    }

    // Inline object
    if (t.startsWith("{") && t.endsWith("}")) {
      return "[String: Any]";
    }

    // Interface type
    const ifaceNames = new Set(interfaces.map((i) => i.name));
    if (ifaceNames.has(t)) {
      if (moduleName) {
        const pascal = capitalizeFirst(moduleName);
        return `${pascal}${t}`;
      }
      return t;
    }

    // Fallback
    return "[String: Any]";
  }

  /**
   * Gets Swift default value for a type
   */
  function getSwiftDefaultValue(swiftType: string): string {
    switch (swiftType) {
      case "Double":
        return "0.0";
      case "Int":
        return "0";
      case "Float":
        return "0.0";
      case "Bool":
        return "false";
      case "String":
        return '""';
      case "[String: Any]":
        return "[String: Any]()";
      case "[Any]":
        return "[Any]()";
      default:
        if (swiftType.startsWith("[")) {
          return "[]";
        }
        return "[String: Any]()";
    }
  }

  /**
   * Gets Objective-C default return value for a type
   */
  function getDefaultReturnValue(tsType: string): string {
    switch (tsType) {
      case "string":
        return '@""';
      case "number":
        return "@(0)";
      case "boolean":
        return "@(NO)";
      case "void":
        return "";
      default:
        if (tsType.includes("[]")) {
          return "@[]";
        }
        return "nil";
    }
  }

  return {
    mapTypeScriptToSwift,
    mapTypeScriptToSwiftForObjC,
    mapTypeScriptToSwiftWithInterfaces,
    mapTypeScriptToObjC,
    mapTsToSwiftForCallbackArg,
    getSwiftDefaultValue,
    getDefaultReturnValue,
    capitalizeFirst,
  };
}
